import { Lightning, Colors, AppData, Router } from '@lightningjs/sdk';
import { getFontFaceFromStyle } from 'support/textUtils';
import { getImageTextureObj, ttsTagRemoval } from 'support/generalUtils';
import Button from 'components/common/buttons/Button';
import TextLabel from 'components/common/TextLabel';
import { translate } from 'support/translate';
import { constants } from 'aliases';
import { AdvancedErrorMessage } from 'types/api/errorMessages';
import { closeApp } from 'support/appUtils';

const CONTENT_WIDTH = 1001;
const LINE_HEIGHT_MULTIPLIER = 1.2;

const INFO_MARGIN_BOTTOM = 80;
const INFO_ITEM_MARGIN_BOTTOM = 24;
// Lightning text textures pad their height by half the font size, so subtract
// it from the margin to match the design spacing
const TEXT_PADDING_MULTIPLIER = 0.5;

const TITLE_FONT_SIZE = 64;
const BODY_FONT_SIZE = 29;
const DESCRIPTION_MAX_LINES = 2;

const TITLE_MARGIN_BOTTOM =
  INFO_ITEM_MARGIN_BOTTOM - TITLE_FONT_SIZE * TEXT_PADDING_MULTIPLIER;
const BODY_MARGIN_BOTTOM =
  INFO_ITEM_MARGIN_BOTTOM - BODY_FONT_SIZE * TEXT_PADDING_MULTIPLIER;

const QR_CODE_SIZE = 266;

const BUTTON_HEIGHT = 70;
const BUTTON_FONT_SIZE = 29;
const BUTTON_PADDINGH = 80;
const BUTTON_MARGIN = 20;
const BUTTON_RADIUS = 8;

// TODO: Replace with the error code reported by the error flow once integrated
const TEST_ERROR_CODE = 'TEST-CODE';

export interface AdvancedErrorModalTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Content: {
    Info: {
      Title: typeof TextLabel;
      Description: typeof TextLabel;
      SupportInfo: typeof TextLabel;
      QrCode: object;
    };
    DismissButton: typeof Button;
  };
}

export default class AdvancedErrorModal
  extends Lightning.Component<AdvancedErrorModalTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<AdvancedErrorModalTemplateSpec>
{
  private _Content = this.getByRef('Content')!;
  private _Info = this._Content.getByRef('Info')!;
  private _Title = this._Info.getByRef('Title')!;
  private _Description = this._Info.getByRef('Description')!;
  private _SupportInfo = this._Info.getByRef('SupportInfo')!;
  private _QrCode = this._Info.getByRef('QrCode')!;
  private _DismissButton = this._Content.getByRef('DismissButton')!;

  get announce() {
    const title = this._Title.text?.text ?? '';
    const description = this._Description.text?.text ?? '';
    const supportInfo = ttsTagRemoval(this._SupportInfo.text?.text ?? '');
    return [title, description, supportInfo];
  }

  static override _template(): Lightning.Component.Template<AdvancedErrorModalTemplateSpec> {
    const bodyText = {
      w: CONTENT_WIDTH,
      wordWrapWidth: CONTENT_WIDTH,
      fontSize: BODY_FONT_SIZE,
      lineHeight: BODY_FONT_SIZE * LINE_HEIGHT_MULTIPLIER,
      fontFace: getFontFaceFromStyle('regular'),
      textColor: Colors('labelText').get(),
      textAlign: 'left' as const,
    };

    return {
      alpha: 0,
      rect: true,
      w: 1920,
      h: 1080,
      color: Colors('modalBackground').get(),
      flex: {
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      zIndex: constants.ui.zIndex.modalLayer,
      Content: {
        flex: { direction: 'column', alignItems: 'center' },
        Info: {
          flex: { direction: 'column', alignItems: 'center' },
          flexItem: { marginBottom: INFO_MARGIN_BOTTOM },
          Title: {
            type: TextLabel,
            flexItem: { marginBottom: TITLE_MARGIN_BOTTOM },
            text: {
              wordWrapWidth: CONTENT_WIDTH,
              fontSize: TITLE_FONT_SIZE,
              lineHeight: TITLE_FONT_SIZE * LINE_HEIGHT_MULTIPLIER,
              fontFace: getFontFaceFromStyle('bold'),
              textColor: Colors('white').get(),
              textAlign: 'center',
            },
          },
          Description: {
            type: TextLabel,
            flexItem: { marginBottom: BODY_MARGIN_BOTTOM },
            text: {
              ...bodyText,
              maxLines: DESCRIPTION_MAX_LINES,
              maxLinesSuffix: translate('global.defaultMaxLineSuffix'),
            },
          },
          SupportInfo: {
            type: TextLabel,
            flexItem: { marginBottom: BODY_MARGIN_BOTTOM },
            text: { ...bodyText, advancedRenderer: true },
          },
          QrCode: {
            w: QR_CODE_SIZE,
            h: QR_CODE_SIZE,
          },
        },
        DismissButton: {
          type: Button,
          height: BUTTON_HEIGHT,
          fontSize: BUTTON_FONT_SIZE,
          paddingH: BUTTON_PADDINGH,
          margin: BUTTON_MARGIN,
          radius: BUTTON_RADIUS,
          backgroundColor: {
            focused: Colors('labelText').get(),
            unfocused: Colors('labelText').get(),
          },
          fontColor: {
            focused: Colors('activeText').get(),
            unfocused: Colors('activeText').get(),
          },
          action: '$onDismiss',
          signals: { $onDismiss: '$onDismiss' },
          zIndex: constants.ui.zIndex.modalLayer + 1,
        },
      },
    };
  }

  override _getFocused() {
    return this._DismissButton;
  }

  override _handleBack() {
    this.$onDismiss();
    return true;
  }

  override _handleKey() {
    // Do not allow the user to bypass the modal
    return true;
  }

  private getErrorMessage(errorCode: string) {
    const errorMessage = AppData?.errorMessages?.find(
      message => message.error_code === errorCode,
    );
    if (!errorMessage) {
      // TODO: Use a fallback error message from the config
    }
    return errorMessage;
  }

  hide() {
    this.patch({ alpha: 0 });
  }

  show() {
    const errorMessage = this.getErrorMessage(TEST_ERROR_CODE);
    if (errorMessage) {
      this.update(errorMessage);
    }
    this.patch({ alpha: 1 });
  }

  update(data: AdvancedErrorMessage) {
    this._Title.patch({ text: { text: data.header_title } });
    this._Description.patch({
      text: {
        text: translate(
          'advancedError.description',
          data.error_code,
          data.error_description,
        ),
      },
    });
    this._SupportInfo.patch({
      text: { text: translate('advancedError.supportInfo') },
    });
    this._QrCode.patch(
      getImageTextureObj(data.qr_code_url, QR_CODE_SIZE, QR_CODE_SIZE),
    );
    this._DismissButton.patch({ label: data.dismiss_button_text });
  }

  $onDismiss() {
    AppData?.modalService.close();
  }
}
